import { useEffect, useState, type CSSProperties } from "react"
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Check,
  Layers3,
  Loader2,
  UserRound,
  LockKeyhole,
  Pencil,
  Archive,
} from "lucide-react"
import { toast } from "sonner"

import {
  RadioGroup as RadioGroupPrimitive,
  ToggleGroup as ToggleGroupPrimitive,
} from "radix-ui"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PageHeader } from "@/components/page-header"
import { PersonalPersonaBuilder } from "@/components/personal-persona-builder"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { ActionCard } from "@/components/media-card"
import { PersonaCard } from "@/components/persona-card"
import { RadioGroup } from "@/components/ui/radio-group"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import {
  useCreateProcessRun,
  usePersonas,
  useArchivePersonalPersona,
} from "@/lib/queries"
import {
  formatFreigabeAb,
  useEinheitFreigabeStatus,
} from "@/lib/einheit-freigabe"
import { useSessionContext } from "@/lib/session"
import { apiFehlerDetail } from "@/lib/api-client"
import { useStartIddTraining } from "@/lib/idd-queries"

import { BereichGate } from "@/components/bereich-gesperrt"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  DIFFICULTY_LABELS,
  DIFFICULTY_ORDER,
  DURCHLAUF_EINHEITEN,
  GESPRAECHSTRAINING_LABEL,
  KI_KUNDE_LABEL,
  IVFP_PHASE_ORDER,
  TRAINING_ANLAESSE,
  TRAINING_EINHEITEN,
  einheitDefinition,
  einheitFuerPhase,
  trainingsEinheiten,
  type AkquiseArt,
  type GespraechsPhase,
  type Persona,
  type TrainingsAnlassKey,
  type TrainingsEinheit,
  iddSzenarioFuerEinheit,
} from "@nl/shared"

const ALL_KEYS = DURCHLAUF_EINHEITEN
const ENABLED_UNITS = new Set<TrainingsEinheit>(ALL_KEYS)

const DIFFICULTY_HINTS: Record<string, string> = {
  entry: "Kooperativ, wenig Gegenwind. Gut zum Einsteigen in eine Phase.",
  medium: "Realistischer Alltag mit den üblichen Einwänden.",
  advanced: "Skeptisch und gut vorbereitet. Fordert saubere Einwandbehandlung.",
}

type Step = 1 | 2 | 3
type StepQuery = "thema" | "phase" | "persona"

// The search param is still called "phase": old links from mails and the coach
// chat must not run into nothing.
const STEP_QUERY: Record<Step, StepQuery> = {
  1: "thema",
  2: "phase",
  3: "persona",
}

const AKQUISE_OPTIONEN: { key: AkquiseArt; label: string; hint: string }[] = [
  {
    key: "bestand",
    label: "Bekannter Kunde",
    hint: "Ihr kennt euch. Rund drei von vier Terminen entstehen so.",
  },
  {
    key: "kaltakquise",
    label: "Erstkontakt",
    hint: "Kunde deiner Sparkasse, aber ihr hattet noch nichts miteinander zu tun.",
  },
]

const DEFAULT_ANLASS: TrainingsAnlassKey = "einkommensgutachten"

// Thema, Phase, Fall. Not "Produkt", however neatly the three P's would have
// read: the IVFP process does not start from a product, the occasion carries the
// conversation and a product only appears in the closing phase (Masterunterlage,
// quoted at IVFP_LEHRGANG).
const STEPS = [
  {
    number: 1 as const,
    label: "Thema",
    icon: Briefcase,
    tour: "training-thema",
  },
  {
    number: 2 as const,
    label: "Phase",
    icon: Layers3,
    tour: "training-einheit",
  },
  {
    number: 3 as const,
    label: KI_KUNDE_LABEL,
    icon: UserRound,
    tour: "training-fall",
  },
]

/**
 * Makes the selection follow the focus, which is what a radio group is
 * supposed to do when arrow keys move through it.
 *
 * Radix intends to do this itself, but it detects the arrow key on a
 * `document` listener while React dispatches its own handlers on the root
 * container — so the focus has already moved by the time Radix knows a key was
 * pressed, and the selection stays behind.
 */
function selectFocusedRadio(onSelect: (value: string) => void) {
  return (event: React.FocusEvent<HTMLElement>) => {
    const value = event.target.closest('[role="radio"]')?.getAttribute("value")
    if (value) onSelect(value)
  }
}

function ProcessRunStepper({
  current,
  values,
  reachableStep,
  onStepChange,
}: {
  current: Step
  values: Partial<Record<Step, string | undefined>>
  /** Highest step the current selection allows; later ones stay locked. */
  reachableStep: Step
  onStepChange: (step: Step) => void
}) {
  return (
    <ol className="flex items-start" aria-label="Konfigurationsschritte">
      {STEPS.map((step, index) => {
        const active = step.number === current
        const done = step.number < current
        const enabled = step.number <= reachableStep
        const Icon = step.icon

        return (
          <li
            key={step.number}
            data-tour={step.tour}
            className="flex items-start"
            aria-current={active ? "step" : undefined}
          >
            <button
              type="button"
              onClick={() => onStepChange(step.number)}
              disabled={!enabled}
              className="flex w-8 min-w-0 items-center gap-3 rounded-md text-left transition-opacity outline-none hover:opacity-75 focus-visible:ring-2 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-45 sm:w-44"
              aria-label={`Schritt ${step.number}: ${step.label}`}
            >
              <span
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors",
                  active && "border-primary bg-primary text-primary-foreground",
                  done && "border-foreground bg-foreground text-background",
                  !active &&
                    !done &&
                    "border-[var(--rule)] text-muted-foreground/50"
                )}
              >
                {done ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Icon className="h-3.5 w-3.5" />
                )}
              </span>
              <span className="hidden min-w-0 sm:block">
                <span
                  className={cn(
                    "flex items-baseline gap-1.5 text-[14px]",
                    active
                      ? "text-foreground"
                      : done
                        ? "text-muted-foreground"
                        : "text-muted-foreground/50"
                  )}
                >
                  <span className="text-[11.5px] text-muted-foreground tabular-nums">
                    0{step.number}
                  </span>
                  <span className="font-medium">{step.label}</span>
                </span>
                {/* Fixed height even when empty, so the row does not jump the
                    moment the first selection gets a value. */}
                <span className="mt-0.5 block h-[13px] truncate text-[12px] leading-[13px] text-muted-foreground">
                  {values[step.number] ?? " "}
                </span>
              </span>
            </button>
            {index < STEPS.length - 1 && (
              <span
                className={cn(
                  "mx-3 mt-4 h-px w-10 shrink-0 transition-colors sm:w-14",
                  done ? "bg-foreground" : "bg-[var(--rule)]"
                )}
                aria-hidden
              />
            )}
          </li>
        )
      })}
    </ol>
  )
}

/**
 * Bekannter Kunde oder Erstkontakt, vor dem Anruf gewählt.
 *
 * Kompakt wie die Baustein-Zeile im Schritt davor: nur die beiden Optionen,
 * ohne Rahmenkarte mit Symbol und Überschrift darüber. Die Überschrift hätte
 * wiederholt, was die Optionen selbst sagen, und die Seite trug damit zwei
 * Karten übereinander für eine Wahl.
 *
 * Die Breite kommt von außen: der Aufrufer setzt den Block in die Spalte der
 * Terminierungs-Kachel, weil die Wahl zu dieser einen Einheit gehört.
 *
 * Die Auswahl markiert wie die Kacheln darüber — Ring plus Häkchen —, damit
 * auf diesem Schritt nur eine Auswahlsprache steht.
 */
function AkquiseKarte({
  value,
  onValueChange,
}: {
  value: AkquiseArt
  onValueChange: (value: AkquiseArt) => void
}) {
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onValueChange(next as AkquiseArt)}
      onFocusCapture={selectFocusedRadio((next) =>
        onValueChange(next as AkquiseArt)
      )}
      aria-label="Bekannter Kunde oder Erstkontakt"
      className="reveal grid gap-2.5 sm:grid-cols-2"
    >
      {AKQUISE_OPTIONEN.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.key}
          value={option.key}
          className="group relative rounded-xl border border-[var(--rule)] bg-card px-4 py-3.5 text-left transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=checked]:ring-2 data-[state=checked]:ring-primary"
        >
          <div className="pr-7 text-[14px] font-medium">{option.label}</div>
          <p className="mt-0.5 pr-7 text-[13px] leading-relaxed text-muted-foreground">
            {option.hint}
          </p>
          <span
            className="absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground opacity-0 shadow-sm transition-opacity group-data-[state=checked]:opacity-100"
            aria-hidden
          >
            <Check className="h-3.5 w-3.5" />
          </span>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroup>
  )
}

type UnitPickerProps = {
  value: TrainingsEinheit[]
  onValueChange: (values: string[]) => void
  freigabe: ReturnType<typeof useEinheitFreigabeStatus>
  /** IDD mode: only units with an IDD module can be picked. */
  nurIdd?: boolean
}

/**
 * The photo fills the tile, the caption sits on a gradient. One tile per
 * training unit, in process order.
 */
function EinheitenKacheln({
  value,
  onValueChange,
  freigabe,
  nurIdd,
}: UnitPickerProps) {
  return (
    <ToggleGroupPrimitive.Root
      type="multiple"
      value={value}
      onValueChange={onValueChange}
      aria-label="Trainingseinheiten wählen"
      className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-5"
    >
      {TRAINING_EINHEITEN.map((phase, index) => {
        const iddFremd = nurIdd && !iddSzenarioFuerEinheit(phase.key)
        const enabled = freigabe.istOffen(phase.key) && !iddFremd
        const status = freigabe.status(phase.key)
        const bild = (
          <>
            <img
              src={phase.imageUrl}
              alt=""
              loading="lazy"
              className={cn(
                "aspect-[3/4] w-full object-cover",
                enabled
                  ? "transition-transform duration-300 group-hover:scale-[1.03]"
                  : "opacity-70 grayscale"
              )}
            />
            {/* Legibility gradient: the caption sits on the photo, so the
                text color is fixed to white in both themes. */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-3 pt-14 pb-2.5 [text-shadow:0_1px_2px_rgb(0_0_0/0.55)]">
              <div className="text-[10.5px] text-white/80 tabular-nums">
                0{index + 1}
              </div>
              <div className="text-[13px] leading-snug font-medium text-white">
                {phase.label}
              </div>
            </div>
          </>
        )

        return (
          <ToggleGroupPrimitive.Item
            key={phase.key}
            value={phase.key}
            // `aria-disabled` rather than `disabled`, for the same reason as in
            // step 1: disabled items fall out of the roving tabindex. The
            // selection handler filters the disabled keys out instead.
            aria-disabled={enabled ? undefined : true}
            className={cn(
              "group relative overflow-hidden rounded-xl text-left ring-1 ring-foreground/10 transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=on]:ring-2 data-[state=on]:ring-primary",
              !enabled && "cursor-not-allowed"
            )}
          >
            {bild}
            <span
              className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground opacity-0 shadow-sm transition-opacity group-data-[state=on]:opacity-100"
              aria-hidden
            >
              <Check className="h-3.5 w-3.5" />
            </span>
            {/* No badge while loading: the tiles are merely inert then, not
                locked. */}
            {iddFremd && freigabe.istOffen(phase.key) && (
              <Badge className="absolute top-2 left-2 border-0 bg-black/55 text-[11px] font-normal text-white">
                Nicht IDD-anrechenbar
              </Badge>
            )}
            {!enabled && !iddFremd && status && (
              <Badge className="absolute top-2 left-2 gap-1 border-0 bg-black/55 text-[11px] font-normal text-white">
                {!phase.verfuegbar ? (
                  "In Vorbereitung"
                ) : (
                  <>
                    <LockKeyhole className="h-3 w-3" aria-hidden />
                    {status.freigabeAb
                      ? `ab ${formatFreigabeAb(status.freigabeAb)}`
                      : "Gesperrt"}
                  </>
                )}
              </Badge>
            )}
          </ToggleGroupPrimitive.Item>
        )
      })}
    </ToggleGroupPrimitive.Root>
  )
}

function DurchlaufStart() {
  const navigate = useNavigate()
  const {
    schritt,
    phase: vorgabePhase,
    persona: vorgabePersona,
    idd,
  } = Route.useSearch()
  const { tenantId, user } = useSessionContext()
  const { data: personas = [], isLoading: personasLoading } = usePersonas()
  const freigabe = useEinheitFreigabeStatus()
  // The coach is picked during onboarding and stored on the user, not per device.
  const createRun = useCreateProcessRun()
  const startIdd = useStartIddTraining()
  const [builderOpen, setBuilderOpen] = useState(false)
  const [editingPersona, setEditingPersona] = useState<Persona>()
  const [archivingPersona, setArchivingPersona] = useState<Persona | null>(null)
  const archivePersona = useArchivePersonalPersona()

  const [anlassKey, setAnlassKey] = useState<TrainingsAnlassKey>(DEFAULT_ANLASS)
  // Prefilled from the coach's proposal card ("Anpassen"); otherwise the
  // defaults. Read once: later steps own the state.
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(
    vorgabePersona ?? null
  )
  // Preset from the coach's suggestion card. It names a phase, because the quiz
  // still points at phases; here it becomes that phase's unit.
  const vorgabeEinheit = vorgabePhase
    ? einheitFuerPhase(vorgabePhase)
    : undefined
  const [selectedUnits, setSelectedUnits] = useState<Set<TrainingsEinheit>>(
    () =>
      new Set<TrainingsEinheit>(
        vorgabeEinheit && ENABLED_UNITS.has(vorgabeEinheit)
          ? [vorgabeEinheit]
          : []
      )
  )
  const [akquise, setAkquise] = useState<AkquiseArt>("bestand")

  // Guards the keyboard path as well: Radix moves the selection with the arrow
  // keys, so refusing the click alone would let someone land on a preview.
  const selectAnlass = (value: string) => {
    const anlass = TRAINING_ANLAESSE.find((entry) => entry.key === value)
    if (anlass?.available) setAnlassKey(anlass.key)
  }

  const activePersonas = personas.filter(
    (persona) => persona.status === "active"
  )
  const canCreatePersona =
    !personasLoading && activePersonas.some((p) => !p.isPersonal)
  const selectedPersona = personas.find(
    (persona) => persona.id === selectedPersonaId
  )

  // Empty difficulties are dropped so no tenant sees a heading with nothing
  // under it.
  const personasByDifficulty = DIFFICULTY_ORDER.map((difficulty) => ({
    difficulty,
    personas: activePersonas.filter(
      (persona) => persona.difficulty === difficulty && !persona.isPersonal
    ),
  })).filter((group) => group.personas.length > 0)

  // Filtered here rather than in the state: the selection is initialised
  // before the release state has loaded, and a unit that unlocks while the
  // page is open should come back selected.
  const units: TrainingsEinheit[] = ALL_KEYS.filter(
    (key) => selectedUnits.has(key) && freigabe.istOffen(key)
  )
  const gesperrt = TRAINING_EINHEITEN.filter(
    (e) => e.verfuegbar && freigabe.status(e.key) && !freigabe.istOffen(e.key)
  )
  // The switch only shows where it changes something: it touches the opening
  // sentences of the phone call and nothing else.
  const zeigtAkquise = units.some(
    (key) => einheitDefinition(key).kanal === "telefon"
  )

  const step: Step = schritt === "persona" ? 3 : schritt === "phase" ? 2 : 1
  // The occasion always has a value, so the phase step is open from the start;
  // the case step needs at least one phase.
  const reachableStep: Step = units.length > 0 ? 3 : 2

  const setStep = (nextStep: Step, replace = false) => {
    if (nextStep > reachableStep) return
    void navigate({
      to: "/durchlauf",
      search: { schritt: STEP_QUERY[nextStep], idd },
      replace,
    })
  }

  useEffect(() => {
    // Until the release state is in, no unit counts as selected, which would
    // bounce a reload on the case step back to the start.
    if (freigabe.isLoading) return
    if (!schritt || step > reachableStep) {
      void navigate({
        to: "/durchlauf",
        search: { schritt: STEP_QUERY[1], idd },
        replace: true,
      })
    }
  }, [freigabe.isLoading, idd, navigate, reachableStep, schritt, step])

  // Mirrors the `aria-disabled` on the unit tiles: the items stay clickable
  // for Radix, so the disabled keys are dropped here instead.
  const selectUnits = (values: string[]) => {
    const erlaubt = (values as TrainingsEinheit[]).filter(
      (key) =>
        ENABLED_UNITS.has(key) &&
        freigabe.istOffen(key) &&
        (!idd || iddSzenarioFuerEinheit(key))
    )
    // IDD mode credits exactly one unit, so a new pick replaces the old one.
    const neu = idd
      ? erlaubt.filter((key) => !selectedUnits.has(key)).slice(-1)
      : erlaubt
    setSelectedUnits(new Set(idd && neu.length === 0 ? erlaubt : neu))
  }

  const start = () => {
    if (createRun.isPending || startIdd.isPending) return
    if (units.length === 0) {
      toast.error("Bitte mindestens eine Trainingseinheit wählen.")
      setStep(2)
      return
    }
    if (!selectedPersonaId) {
      toast.error("Bitte einen Fall wählen.")
      return
    }

    const durchlauf = {
      personaId: selectedPersonaId,
      units,
      ...(zeigtAkquise ? { akquise } : {}),
      coachStyleId: user?.coachStyleId ?? undefined,
    }
    const iddModul = idd ? iddSzenarioFuerEinheit(units[0]) : undefined
    if (iddModul) {
      startIdd
        .mutateAsync({
          art: "gespraech",
          modulKey: iddModul.key,
          durchlauf: { ...durchlauf, mode: "text" },
        })
        .then((training) => {
          if (training.processRunId) {
            void navigate({
              to: "/durchlauf/$runId",
              params: { runId: training.processRunId },
            })
          }
        })
        .catch((error: unknown) =>
          toast.error(
            apiFehlerDetail(error) ??
              "Das IDD-Gesprächstraining konnte nicht gestartet werden."
          )
        )
      return
    }

    createRun.mutate(
      durchlauf,
      {
        onSuccess: (run) =>
          void navigate({ to: "/durchlauf/$runId", params: { runId: run.id } }),
        onError: () =>
          toast.error(
            `Das ${GESPRAECHSTRAINING_LABEL} konnte nicht gestartet werden.`
          ),
      }
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-5">
      <PageHeader
        eyebrow={idd ? "IDD-Gesprächstraining" : GESPRAECHSTRAINING_LABEL}
        description={
          idd
            ? "Wähle eine Einheit und deinen KI-Kunden. Angerechnet wird die Einheit einmal im Jahr, wenn alle Pflichtschritte erreicht sind."
            : undefined
        }
        title={
          step === 1
            ? "Was möchtest du heute mit mir trainieren?"
            : step === 2
              ? "Welchen Teil des Gesprächs möchtest du üben?"
              : "Welchen Fall möchtest du üben?"
        }
      >
        <div>
          <ProcessRunStepper
            current={step}
            reachableStep={reachableStep}
            onStepChange={setStep}
            values={{
              1: TRAINING_ANLAESSE.find((entry) => entry.key === anlassKey)
                ?.shortLabel,
              // With a single unit its name says more than a count.
              2:
                units.length === 1
                  ? einheitDefinition(units[0]).shortLabel
                  : `${units.length} von ${ALL_KEYS.length} Phasen`,
              3: selectedPersona?.name,
            }}
          />
        </div>
      </PageHeader>

      {step === 1 && (
        <section
          className="reveal flex flex-1 flex-col gap-4"
          aria-label="Thema"
        >
          <RadioGroup
            value={anlassKey}
            onValueChange={selectAnlass}
            onFocusCapture={selectFocusedRadio(selectAnlass)}
            aria-label="Beratungsanlass"
            className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3"
          >
            {TRAINING_ANLAESSE.map((anlass) => (
              <RadioGroupPrimitive.Item
                key={anlass.key}
                value={anlass.key}
                // `aria-disabled` rather than `disabled`: Radix drops disabled
                // items out of the roving tabindex, and an occasion that is only
                // there to be seen would then not exist for keyboard and screen
                // reader users at all.
                aria-disabled={anlass.available ? undefined : true}
                className={cn(
                  "group relative overflow-hidden rounded-xl text-left ring-1 ring-foreground/10 transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=checked]:ring-2 data-[state=checked]:ring-primary",
                  !anlass.available && "cursor-not-allowed"
                )}
              >
                <img
                  src={anlass.imageUrl}
                  alt=""
                  loading="lazy"
                  className={cn(
                    "aspect-[3/2] w-full object-cover",
                    anlass.available
                      ? "transition-transform duration-300 group-hover:scale-[1.03]"
                      : "opacity-70 grayscale"
                  )}
                />
                {/* Legibility gradient, same as the phase tiles: the caption
                    sits on the photo, so the text is fixed to white in both
                    themes. */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-4 pt-20 pb-3.5 [text-shadow:0_1px_2px_rgb(0_0_0/0.55)]">
                  <div className="text-[15px] leading-snug font-medium text-white">
                    {anlass.label}
                  </div>
                  <p className="mt-1 text-[13px] leading-relaxed text-white/90">
                    {anlass.description}
                  </p>
                </div>
                <span
                  className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground opacity-0 shadow-sm transition-opacity group-data-[state=checked]:opacity-100"
                  aria-hidden
                >
                  <Check className="h-3.5 w-3.5" />
                </span>
                {/* Named rather than only greyed out: an unexplained grey card
                    reads as broken, a labelled one as a roadmap. */}
                {!anlass.available && (
                  <Badge className="absolute top-2 left-2 border-0 bg-black/55 text-[11px] font-normal text-white">
                    In Vorbereitung
                  </Badge>
                )}
              </RadioGroupPrimitive.Item>
            ))}
          </RadioGroup>

          <div className="sticky bottom-0 z-20 -mx-8 mt-auto flex min-h-[69px] items-center justify-end border-t border-[var(--rule)] bg-[var(--paper)]/95 px-8 py-3 backdrop-blur-sm">
            <Button type="button" onClick={() => setStep(2)}>
              Weiter zur Phase
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section
          className="reveal flex flex-1 flex-col gap-4"
          aria-label="Phase"
        >
          {/* Multiple choice, so a toggle group rather than a radio group.
              Radix keeps the pressed state and keyboard handling. */}
          <EinheitenKacheln
            value={units}
            onValueChange={selectUnits}
            freigabe={freigabe}
            nurIdd={idd}
          />

          {/* Unter den Kacheln und auf derselben Spalte wie die Terminierung:
              die Akquiseart gehört zu dieser einen Einheit und zu keiner
              anderen. Drei Mittel für dieselbe Aussage, weil jedes allein
              kippen kann — das Raster der Kacheln für die linke Kante, der
              Zeiger aus der Kachel heraus, und Nummer plus Name im Text, wenn
              auf schmalem Schirm beides verloren geht. */}
          {zeigtAkquise && (
            <div className="mt-1 grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-5">
              {/* Zwei Einrückungen aus einer Quelle: die Zeile beginnt hinter
                  der Zeiger-Spalte, die Karten zusätzlich hinter Nummer und
                  Abstand — also genau an der Textkante des Labels. Getrennt
                  gepflegte Werte würden auseinanderlaufen. */}
              <div
                className="col-span-2"
                style={
                  {
                    "--zeiger": "2.75rem",
                    "--nummer": "1.625rem",
                  } as CSSProperties
                }
              >
                <div className="relative mb-1.5 flex min-h-5 items-center gap-1.5 pl-[var(--zeiger)]">
                  {/* Zulaufend: der Bogen kommt aus der Kachel und endet als
                      Spitze am Text. Die Spitze liegt auf der Mittelachse des
                      viewBox, der Zeiger selbst ist absolut und mittig zur
                      Zeile — so trifft er die Mitte der Schrift, ohne mit
                      seiner leeren unteren Hälfte die Zeile aufzublähen.
                      Farbe aus `--rule`, damit er als Linie der Oberfläche
                      liest und nicht als Grafik. */}
                  <svg
                    aria-hidden
                    viewBox="0 0 40 44"
                    className="pointer-events-none absolute top-1/2 left-0 h-11 w-10 -translate-y-1/2 text-[var(--rule)]"
                  >
                    <path
                      d="M8 0 V16 Q8 22 14 22 H29"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path d="M28 18.5 L34 22 L28 25.5 Z" fill="currentColor" />
                  </svg>
                  <span className="flex h-5 w-5 items-center justify-center rounded-sm bg-primary text-[10px] text-primary-foreground tabular-nums">
                    {`0${ALL_KEYS.indexOf("terminierung") + 1}`}
                  </span>
                  <span className="eyebrow">
                    {einheitDefinition("terminierung").shortLabel} · dein
                    Einstieg am Telefon
                  </span>
                </div>
                <div className="pl-[calc(var(--zeiger)+var(--nummer))]">
                  <AkquiseKarte value={akquise} onValueChange={setAkquise} />
                </div>
              </div>
            </div>
          )}
          {units.length > 0 && trainingsEinheiten(units).length === 0 && (
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">
              Wähle mindestens eine Phase, die du trainierst. Die
              Nachbereitung schaut auf das, was davor passiert ist.
            </p>
          )}

          {gesperrt.map((e) => {
            const ab = freigabe.status(e.key)?.freigabeAb
            const fuer =
              freigabe.status(e.key)?.quelle === "gruppe" ? "deine Gruppe" : "deine Sparkasse"
            return (
              <p
                key={e.key}
                className="flex items-center gap-1.5 text-[12.5px] leading-relaxed text-muted-foreground"
              >
                <LockKeyhole className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {ab
                  ? `${e.label} wird am ${formatFreigabeAb(ab)} für ${fuer} freigeschaltet.`
                  : `${e.label} ist für ${fuer} noch nicht freigeschaltet.`}
              </p>
            )
          })}

          <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex min-h-[69px] items-center justify-between gap-2 border-t border-[var(--rule)] bg-[var(--paper)]/95 px-4 py-3 backdrop-blur-sm sm:-mx-8 sm:px-8">
            <Button type="button" variant="ghost" onClick={() => setStep(1)}>
              <ArrowLeft className="h-4 w-4" />
              Thema ändern
            </Button>
            <Button
              type="button"
              // Die Nachbereitung allein ist kein Durchlauf: sie zeigt die
              // Auswertung über die Einheiten davor.
              disabled={trainingsEinheiten(units).length === 0}
              onClick={() => setStep(3)}
            >
              Weiter zum Fall
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </section>
      )}

      {step === 3 && (
        <section
          className="reveal flex flex-1 flex-col gap-4"
          aria-label="Fallauswahl"
        >
          {/* One group across all difficulty rows: the rows only structure the
              view, the choice is a single one. Radix supplies the roving
              tabindex and arrow-key movement across the rows. */}
          <RadioGroup
            value={selectedPersonaId ?? ""}
            onValueChange={setSelectedPersonaId}
            onFocusCapture={selectFocusedRadio(setSelectedPersonaId)}
            aria-label="Fall auswählen"
            className="block space-y-7"
          >
            <section className="space-y-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="font-display text-lg font-medium">
                  Deine KI-Kunden
                </h2>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <LockKeyhole size={12} /> Nur für dich
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {activePersonas
                    .filter((p) => p.isPersonal)
                    .map((persona) => (
                      <div key={persona.id} className="relative min-w-0">
                        <PersonaCard persona={persona} className="w-full" />
                        {/* Über der Karte statt darin: `PersonaCard` ist ein
                            Radio-Item, also selbst eine Schaltfläche, und
                            Schaltflächen darin wären ungültig verschachtelt
                            und für die Tastatur nicht erreichbar.

                            `right-10` hält die Auswahlmarkierung der Karte
                            frei, die auf `right-2 top-2` sitzt.

                            Dunkler Vorsatz mit Weichzeichner, weil die Ecke
                            auf dem Foto liegt und je Kunde anders hell ist.
                            75 % und nicht die 55 % des Badges daneben: über
                            einer weißen Fotostelle kommt ein weißes Symbol auf
                            black/55 nur auf 2,1:1, WCAG fordert für Symbole
                            3:1. Mit 75 % sind es 3,5:1, auch im schlechtesten
                            Fall. */}
                        <div className="absolute top-2 right-10 flex gap-1 rounded-full bg-black/75 p-0.5 ring-1 ring-white/15 backdrop-blur-sm">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                // `after:` vergrößert die Trefffläche über das
                                // Sichtbare hinaus, wie beim Schalter und beim
                                // Reglergriff — in der Kartenecke ist kein
                                // Platz für 44 px sichtbare Kantenlänge.
                                className="relative size-8 rounded-full text-white after:absolute after:-inset-1.5 hover:bg-black/40 hover:text-white focus-visible:ring-2 focus-visible:ring-white/70"
                                onClick={() => {
                                  setEditingPersona(persona)
                                  setBuilderOpen(true)
                                }}
                                aria-label={`${persona.name} bearbeiten`}
                              >
                                <Pencil size={14} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Bearbeiten</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="relative size-8 rounded-full text-white after:absolute after:-inset-1.5 hover:bg-black/40 hover:text-white focus-visible:ring-2 focus-visible:ring-white/70"
                                onClick={() => setArchivingPersona(persona)}
                                aria-label={`${persona.name} archivieren`}
                              >
                                <Archive size={14} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Archivieren</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                <button
                  type="button"
                  className="media-card-action"
                  onClick={() => {
                    setEditingPersona(undefined)
                    setBuilderOpen(true)
                  }}
                  disabled={!canCreatePersona}
                  aria-disabled={!canCreatePersona}
                >
                  <ActionCard>Eigenen KI-Kunden erstellen</ActionCard>
                </button>
              </div>
            </section>
            {/* A wrapping grid rather than a scroll rail, same as the theme
                step: three stretched tiles per row leave no dead space on the
                right. */}
            {personasByDifficulty.map((group) => (
              <section key={group.difficulty} className="space-y-3">
                <div>
                  <h2 className="font-display text-[17px] leading-tight font-medium tracking-tight">
                    {DIFFICULTY_LABELS[group.difficulty]}
                  </h2>
                  <p className="mt-0.5 text-[14px] leading-relaxed text-muted-foreground">
                    {DIFFICULTY_HINTS[group.difficulty]}
                  </p>
                </div>
                <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                  {group.personas.map((persona) => (
                    <PersonaCard key={persona.id} persona={persona} />
                  ))}
                </div>
              </section>
            ))}
          </RadioGroup>

          <PersonalPersonaBuilder
            open={builderOpen}
            onOpenChange={setBuilderOpen}
            personas={personas}
            initial={editingPersona}
            onSaved={(persona) => setSelectedPersonaId(persona.id)}
          />
          <Dialog
            open={!!archivingPersona}
            onOpenChange={(open) => {
              if (!open && !archivePersona.isPending) setArchivingPersona(null)
            }}
          >
            <DialogContent>
              <DialogTitle>KI-Kunden aus der Auswahl entfernen?</DialogTitle>
              <DialogDescription>
                {archivingPersona?.name} wird archiviert. Deine bisherigen
                Trainings bleiben erhalten.
              </DialogDescription>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={archivePersona.isPending}
                  onClick={() => setArchivingPersona(null)}
                >
                  Behalten
                </Button>
                <Button
                  disabled={archivePersona.isPending}
                  onClick={async () => {
                    if (!archivingPersona) return
                    try {
                      await archivePersona.mutateAsync(archivingPersona.id)
                      if (selectedPersonaId === archivingPersona.id)
                        setSelectedPersonaId(null)
                      setArchivingPersona(null)
                      toast.success("KI-Kunde archiviert")
                    } catch {
                      toast.error(
                        "Archivieren nicht möglich. Bitte versuche es erneut."
                      )
                    }
                  }}
                >
                  {archivePersona.isPending
                    ? "Wird archiviert …"
                    : "Archivieren"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {!personasLoading && activePersonas.length === 0 && (
            <p className="rounded-md border border-[var(--rule)] bg-muted/20 p-5 text-[14px] text-muted-foreground">
              Bei deinem Mandanten ({tenantId ?? "—"}) sind noch keine Fälle
              hinterlegt.
            </p>
          )}

          <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex min-h-[69px] items-center justify-between gap-2 border-t border-[var(--rule)] bg-[var(--paper)]/95 px-4 py-3 backdrop-blur-sm sm:-mx-8 sm:px-8">
            <Button type="button" variant="ghost" onClick={() => setStep(2)}>
              <ArrowLeft className="h-4 w-4" />
              Phase ändern
            </Button>
            <Button
              type="button"
              data-tour="training-start"
              onClick={start}
              disabled={createRun.isPending || !selectedPersona}
              className="min-w-0 sm:min-w-[240px]"
            >
              {createRun.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              <span className="sm:hidden">Training starten</span>
              <span className="hidden sm:inline">
                {GESPRAECHSTRAINING_LABEL} starten
              </span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </section>
      )}
    </div>
  )
}

function DurchlaufStartGesperrt() {
  return (
    <BereichGate bereich="gespraechstraining">
      <DurchlaufStart />
    </BereichGate>
  )
}

export const Route = createFileRoute("/_app/durchlauf/")({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    schritt?: StepQuery
    phase?: GespraechsPhase
    persona?: string
    idd?: boolean
  } => ({
    schritt:
      search.schritt === "thema" ||
      search.schritt === "phase" ||
      search.schritt === "persona"
        ? search.schritt
        : undefined,
    phase: IVFP_PHASE_ORDER.includes(search.phase as GespraechsPhase)
      ? (search.phase as GespraechsPhase)
      : undefined,
    persona:
      typeof search.persona === "string" && search.persona.trim()
        ? search.persona.trim()
        : undefined,
    idd: search.idd === true || search.idd === "true" ? true : undefined,
  }),
  component: DurchlaufStartGesperrt,
})
